/**
 * cutterSelectorMatrix.js — Sandvik raiseboring cutter → rock-condition
 * recommendation matrix, extracted verbatim from Sandvik's
 * "cutter selection for different rock conditions" reference PNG
 * (Rotary raiseboring folder, Marcom-approved 2024).
 *
 * The matrix maps rock properties (UCS, CAI, or plain-English formation
 * label) to the cutter models that Sandvik recommends. Consumed by
 * `CutterSelectorMatrix.jsx` on the raiseboring-cutters landing.
 */

export const CUTTER_TIERS = [
  {
    id: 'not-very',
    label: 'Not very abrasive',
    formation: 'Soft',
    ucs: { min: 0,   max: 100, display: '< 100 MPa' },
    cai: { min: 0.3, max: 0.5, display: '0.3 – 0.5' },
  },
  {
    id: 'slightly',
    label: 'Slightly abrasive',
    formation: 'Medium',
    ucs: { min: 100, max: 140, display: '100 – 140 MPa' },
    cai: { min: 0.5, max: 1.0, display: '0.5 – 1.0' },
  },
  {
    id: 'medium',
    label: 'Medium abrasive',
    formation: 'Medium hard',
    ucs: { min: 140, max: 180, display: '140 – 180 MPa' },
    cai: { min: 1.0, max: 2.0, display: '1.0 – 2.0' },
  },
  {
    id: 'very',
    label: 'Very abrasive',
    formation: 'Hard',
    ucs: { min: 180, max: 240, display: '180 – 240 MPa' },
    cai: { min: 2.0, max: 4.0, display: '2.0 – 4.0' },
  },
  {
    id: 'extremely',
    label: 'Extremely abrasive',
    formation: 'Very hard',
    ucs: { min: 240, max: 999, display: '> 240 MPa' },
    cai: { min: 4.0, max: 6.0, display: '4.0 – 6.0' },
  },
];

export const CUTTER_RECOMMENDATIONS = [
  {
    id: 'cmr41-52',
    label: 'CMR41 / CMR52',
    description: 'Single-row button cutter pair — CMR41 and CMR52 mount in alternating positions on the reamer head. General-purpose for medium-to-hard abrasive rock.',
    matches: ['medium', 'very', 'extremely'],
    // Product itemCodes (from products-raiseboring.json) so clicking a
    // recommended cutter can deep-link to its PDP.
    itemCodes: ['7008-6141-77', '7008-6152-77'],
  },
  {
    id: 'cmr601',
    label: 'CMR601',
    description: 'Self-paired multi-row cutter for hard rock. Runs in a single-model configuration across all positions.',
    matches: ['slightly', 'medium', 'very'],
    itemCodes: ['7008-6561-77'],
  },
  {
    id: 'cmr501',
    label: 'CMR501',
    description: 'Self-paired multi-row cutter for soft to medium-hard rock. Runs in a single-model configuration across all positions.',
    matches: ['not-very', 'slightly', 'medium'],
    itemCodes: ['7008-6551-77'],
  },
];

export const GLOSSARY = {
  UCS: 'Uniaxial Compressive Strength — the mechanical property widely used to evaluate rock stability against loads. Higher UCS means harder rock.',
  CAI: 'Cerchar Abrasivity Index — the standardised test that classifies how abrasive a rock material is. Higher CAI means the rock will wear cutters faster.',
};
